"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = exports.useConfigureComments = void 0;

var _react = require("react");

var _commentsSdk = require("@ccx-public/comments-sdk");

var _comments = require("../../actions/comments");

var _useIngest = _interopRequireDefault(require("../../hooks/useIngest"));

var _reducers = require("../../reducers");

var _AppContext = _interopRequireDefault(require("../../context/AppContext"));

var _CallbackContext = _interopRequireDefault(require("../../context/CallbackContext"));

var _usePrevious = _interopRequireDefault(require("../../hooks/usePrevious"));

var _constants = require("../../constants");

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

/***************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
var useConfigureComments = function useConfigureComments() {
  var _useContext = (0, _react.useContext)(_AppContext.default),
      authenticateFetch = _useContext.authenticateFetch,
      appKey = _useContext.appKey,
      appName = _useContext.appName,
      _useContext$assetData = _useContext.assetData,
      assetData = _useContext$assetData === void 0 ? {} : _useContext$assetData,
      disableCCXWorkflow = _useContext.disableCCXWorkflow,
      environment = _useContext.environment,
      _useContext$imsData = _useContext.imsData,
      imsData = _useContext$imsData === void 0 ? {} : _useContext$imsData,
      invitationServiceAccessToken = _useContext.invitationServiceAccessToken,
      pollForUpdates = _useContext.pollForUpdates;

  (0, _react.useEffect)(function () {
    _commentsSdk.Client.instance.configure({
      token: imsData.token,
      userId: imsData.id,
      types: [],
      environment: environment,
      disableGetAuthorization: !authenticateFetch,
      pollForUpdates: pollForUpdates,
      commentType: ['open', 'resolved'],
      appKey: appKey,
      appName: appName,
      assetId: assetData.id,
      validNodes: assetData.validNodes,
      useNodeIndex: assetData.useNodeIndex,
      platform: assetData.isAcpAsset ? 'acp' : 'sc',
      isCCXWorkflow: !disableCCXWorkflow,
      invitationServiceAccessToken: invitationServiceAccessToken
    });
  }, [authenticateFetch, appKey, appName, assetData, disableCCXWorkflow, environment, imsData, invitationServiceAccessToken, pollForUpdates]);
};

exports.useConfigureComments = useConfigureComments;

var useFetchComments = function useFetchComments(enablePolling, disablePolling) {
  var _useContext2 = (0, _react.useContext)(_AppContext.default),
      _useContext2$asset = _useContext2.asset,
      asset = _useContext2$asset.asset,
      setAsset = _useContext2$asset.setAsset,
      _useContext2$assetDat = _useContext2.assetData,
      assetData = _useContext2$assetDat === void 0 ? {} : _useContext2$assetDat,
      _useContext2$imsData = _useContext2.imsData,
      imsData = _useContext2$imsData === void 0 ? {} : _useContext2$imsData,
      environment = _useContext2.environment,
      isOffline = _useContext2.isOffline,
      hasReviewAccess = _useContext2.hasReviewAccess,
      disableCCXWorkflow = _useContext2.disableCCXWorkflow,
      invitationServiceAccessToken = _useContext2.invitationServiceAccessToken,
      locale = _useContext2.locale,
      pollForUpdates = _useContext2.pollForUpdates,
      cacheComments = _useContext2.cacheComments,
      imageCapture = _useContext2.imageCapture,
      globalCommentsToggle = _useContext2.globalCommentsToggle,
      globalComments = _useContext2.globalComments,
      globalCommentsUnmapped = _useContext2.globalCommentsUnmapped;

  var _useContext3 = (0, _react.useContext)(_CallbackContext.default),
      onCommentsSuccess = _useContext3.onCommentsSuccess;

  var ccxIngest = (0, _useIngest.default)();
  var dispatch = (0, _reducers.useDispatch)();
  var reduxState = (0, _reducers.useStore)().getState();
  var lastRequestAssetId = reduxState["".concat(_constants.PREFIX, ":delta")].lastRequestAssetId;
  var prevIsOfflineRef = (0, _react.useRef)();
  var prevImsData = (0, _usePrevious.default)(imsData) || {};
  (0, _react.useEffect)(function () {
    if (hasReviewAccess && assetData.id && !(prevImsData.isGuestUser && !(imsData.isGuestUser && imsData.isLoggedIn)) && !imsData.isGuestUser) {
      prevIsOfflineRef.current = isOffline;
      dispatch((0, _comments.commentsGet)(assetData, isOffline, cacheComments, imsData.id, imageCapture, globalComments, globalCommentsToggle.isActive, globalCommentsUnmapped)).then(function (respAsset) {
        setAsset(respAsset);
      }).catch(function () {
        setAsset(undefined);
      });
    } // disabling for nodeId, imageCapture, isOffline
    //eslint-disable-next-line react-hooks/exhaustive-deps

  }, [dispatch, ccxIngest, assetData.id, imsData, environment, hasReviewAccess, disableCCXWorkflow, invitationServiceAccessToken, locale, pollForUpdates, cacheComments, isOffline]); // clear comments when there is no asset id but comments have been fetched

  (0, _react.useEffect)(function () {
    if (!assetData.id && asset) {
      dispatch((0, _comments.commentsClear)());
      setAsset(undefined);
    }
  }, [dispatch, assetData.id, asset, setAsset]); // notify host comments have been fetched

  (0, _react.useEffect)(function () {
    if (asset && onCommentsSuccess) {
      onCommentsSuccess({
        assetId: lastRequestAssetId
      });
    } // disabling for onCommentsSuccess. only fire when asset changes
    //eslint-disable-next-line react-hooks/exhaustive-deps

  }, [asset, lastRequestAssetId]);
  (0, _react.useEffect)(function () {
    if (!isOffline) {
      asset === null || asset === void 0 ? void 0 : asset.checkForUpdates();
    } // disabling for asset, we only want to run when isOffline changes
    //eslint-disable-next-line react-hooks/exhaustive-deps

  }, [isOffline]); // subscribe to updates on asset object

  (0, _react.useEffect)(function () {
    asset === null || asset === void 0 ? void 0 : asset.on('update', function (data) {
      dispatch((0, _comments.commentsUpdate)(data, assetData.id, cacheComments));
    }); // fire navigation event if active node has comments that arent resolved

    return function () {
      asset === null || asset === void 0 ? void 0 : asset.disableUpdates();
    };
  }, [asset, assetData.id, cacheComments, dispatch]); // add blur/focus events for polling

  (0, _react.useEffect)(function () {
    window.addEventListener('focus', enablePolling);
    window.addEventListener('blur', disablePolling);
    return function () {
      window.removeEventListener('focus', enablePolling);
      window.removeEventListener('blur', disablePolling);
    };
  }, [asset, enablePolling, disablePolling]);
  (0, _react.useEffect)(function () {
    if (assetData.id) {
      enablePolling();
    } else {
      disablePolling();
    } // disabling for callbacks, we only want to run when assetData.id changes
    //eslint-disable-next-line react-hooks/exhaustive-deps

  }, [assetData.id]);
};

var _default = useFetchComments;
exports.default = _default;